import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WidgetFooter from '../../components/WidgetFooter';
import { toast } from 'sonner';
import { getAssetUrl } from '../../lib/assetUrl';
import { 
  Leaf, ChevronRight, ChevronLeft, Check, User, Clock, 
  Calendar, Send, Loader2, Info, Euro, Repeat, MapPin, Phone, Mail,
  Flower, Scissors, FileText, CalendarClock, CreditCard
} from 'lucide-react';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { useClientSavedAddresses } from '../../hooks/useClientSavedAddresses';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { widgetApi } from '../../lib/api';
import { getImageUrl } from '../../lib/imageUtils';
import { useEmbedAutoresize } from '../../lib/widgetEmbed';

const STEPS = [
  { id: 'service', title: 'Service', icon: Clock },
  { id: 'datetime', title: 'Date & Heure', icon: Calendar },
  { id: 'info', title: 'Informations', icon: User },
  { id: 'confirm', title: 'Confirmation', icon: Check },
];

// Steps for quote request (Aménagement path)
const QUOTE_STEPS = [
  { id: 'services', title: 'Services', icon: Flower },
  { id: 'rdv', title: 'RDV Commercial', icon: CalendarClock },
  { id: 'contact', title: 'Vos coordonnées', icon: User },
  { id: 'confirm', title: 'Confirmation', icon: Check },
];

export default function PunctualWidget() {
  const { widgetId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const isEmbedded = searchParams.get('embed') === 'true';
  const prefillName = searchParams.get('prefill_name') || '';
  const prefillEmail = searchParams.get('prefill_email') || '';
  const prefillPhone = searchParams.get('prefill_phone') || '';
  const prefillAddress = searchParams.get('prefill_address') || '';

  useEmbedAutoresize(isEmbedded);
  const { addresses: savedAddresses } = useClientSavedAddresses();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // 'not_found' | 'network' | null
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // New state for service family choice (Entretien vs Aménagement)
  const [serviceChoice, setServiceChoice] = useState(null); // null = not chosen, 'entretien', 'amenagement'
  const [serviceFamilies, setServiceFamilies] = useState([]);
  
  // State for quote request (Aménagement path)
  const [quoteStep, setQuoteStep] = useState(0);
  const [commercialSlots, setCommercialSlots] = useState([]);
  const [commercialInfo, setCommercialInfo] = useState(null);
  
  // State for work hours availability (Entretien path)
  const [workingDays, setWorkingDays] = useState(null); // null = loading, [] = closed all days, [0,1,2,3,4] = Mon-Fri
  const [workingDaysPeriods, setWorkingDaysPeriods] = useState({}); // per-day: {0: {morning, afternoon, full_day}, ...}
  const [dateAvailability, setDateAvailability] = useState(null); // Availability for selected date
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [slotTypes, setSlotTypes] = useState([]); // Custom slot types from company config
  const [closures, setClosures] = useState([]); // Company closures + French holidays
  
  const [quoteData, setQuoteData] = useState({
    selected_services: [],
    appointment_date: null,
    appointment_time: '',
    client_name: prefillName,
    client_email: prefillEmail,
    client_phone: prefillPhone,
    client_address: prefillAddress,
    client_coords: null,
    request_description: '',
    custom_answers: {},
  });
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    service_ids: [], // Multiple services
    client_name: prefillName,
    client_email: prefillEmail,
    client_phone: prefillPhone,
    date: null,
    slot_type: '', // 'half_day' (3.5h) or 'full_day' (7h)
    slot_period: '', // 'morning' or 'afternoon' for half_day
    notes: '',
    custom_answers: {},
    preferred_collaborator_id: '',
    // Recurrence fields
    is_recurring: false,
    recurrence_type: '', // 'weekly', 'biweekly', 'monthly', 'custom'
    recurrence_days: [], // 0-6 for days of week
    recurrence_interval: 7, // for custom: every X days
    recurrence_end_type: 'count', // 'count', 'date', 'unlimited'
    recurrence_count: 4, // number of occurrences
    recurrence_end_date: '' // YYYY-MM-DD
  });

  // Selected services details
  const [selectedServices, setSelectedServices] = useState([]);
  
  // Collaborators filtered by service
  const [filteredCollaborators, setFilteredCollaborators] = useState([]);
  // Collaborators eligible for the service (ignoring date/time) — used to
  // display busy collabs as greyed-out entries instead of hiding them.
  const [serviceCollaborators, setServiceCollaborators] = useState([]);

  // Theme colors from widget config
  const primaryColor = config?.widget?.primary_color || '#2F5233';
  const secondaryColor = config?.widget?.secondary_color || '#D97757';
  const bgColor = config?.widget?.bg_color || '#F9F8F6';
  const textColor = config?.widget?.text_color || '#1a1a1a';
  
  // Default hourly rate from widget or company config
  const defaultHourlyRate = config?.widget?.hourly_rate || config?.company?.default_hourly_rate || 35;

  // Widget-level pricing mode helpers
  const widgetPricingMode = config?.widget?.pricing_mode || 'hourly';
  const widgetSlotTypePrices = config?.widget?.slot_type_prices || {};
  const widgetServicePrices = config?.widget?.service_prices || {};

  // Get price for a given slot type, depending on widget pricing mode
  const getSlotTypePrice = (st) => {
    if (widgetPricingMode === 'per_slot_type') {
      return widgetSlotTypePrices[st.id] ?? widgetSlotTypePrices[st.code] ?? 0;
    }
    return (st.duration_hours || 0) * defaultHourlyRate * (st.required_collaborators || 1);
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await widgetApi.getPunctualConfig(widgetId);
        setConfig(response.data);
        setClosures(response.data.closures || []);
        
        // Load slot types from config - filter by widget.enabled_slot_types if set
        const widgetEnabledSlotIds = response.data.widget?.enabled_slot_types || [];
        const allSlotTypes = response.data.slot_types || [];
        let resolvedSlotTypes = allSlotTypes;
        if (widgetEnabledSlotIds.length > 0) {
          resolvedSlotTypes = allSlotTypes.filter(st => widgetEnabledSlotIds.includes(st.id));
        }
        if (resolvedSlotTypes.length > 0) {
          setSlotTypes(resolvedSlotTypes);
        } else {
          // Default slot types
          setSlotTypes([
            { code: 'half_day', name: 'Demi-journée', duration_hours: 4, required_collaborators: 1 },
            { code: 'full_day', name: 'Journée complète', duration_hours: 7, required_collaborators: 1 }
          ]);
        }
        
        // Check if widget should skip the dispatcher screen
        // If skip_dispatcher is true OR widget has no recurring option, go directly to ponctuel
        const widgetConfig = response.data.widget;
        if (widgetConfig?.skip_dispatcher === true) {
          setServiceChoice('ponctuel');
        }
        // Otherwise serviceChoice stays null to show the choice screen
        
      } catch (error) {
        console.error('Error loading widget config:', error);
        const status = error?.response?.status;
        setLoadError(status === 404 ? 'not_found' : 'network');
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, [widgetId]);

  // When the widget exposes a single service, pre-select it so the user can
  // simply hit "Suivant" without clicking. We deliberately do NOT auto-jump
  // to step 1: keeping the user on step 0 preserves context (service title,
  // description, pricing hints) and avoids disorientation.
  useEffect(() => {
    if (!config?.services || config.services.length !== 1) return;
    const onlyId = config.services[0].id;
    setFormData((prev) => {
      if (prev.service_ids && prev.service_ids.length > 0) return prev;
      return { ...prev, service_ids: [onlyId], selected_services: [onlyId] };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // Fetch commercial availability for quote requests (Aménagement path)
  useEffect(() => {
    if (serviceChoice === 'amenagement' && quoteData.appointment_date) {
      const fetchCommercialSlots = async () => {
        try {
          const dateStr = quoteData.appointment_date.toISOString().split('T')[0];
          const response = await widgetApi.getCommercialAvailability(widgetId, dateStr);
          setCommercialSlots(response.data.slots || []);
          setCommercialInfo(response.data.commercial);
        } catch (error) {
          console.error('Error fetching commercial slots:', error);
          setCommercialSlots([]);
        }
      };
      fetchCommercialSlots();
    }
  }, [serviceChoice, quoteData.appointment_date, widgetId]);

  // Fetch work hours configuration (for Entretien path - to know which days are open)
  useEffect(() => {
    const fetchWorkHours = async () => {
      try {
        const response = await widgetApi.getWorkAvailability(widgetId);
        setWorkingDays(response.data.working_days_list || [0, 1, 2, 3, 4]); // Default Mon-Fri
        setWorkingDaysPeriods(response.data.working_days_periods || {});
      } catch (error) {
        console.error('Error fetching work hours:', error);
        setWorkingDays([0, 1, 2, 3, 4]); // Default to Mon-Fri on error
        setWorkingDaysPeriods({});
      }
    };
    if (widgetId) {
      fetchWorkHours();
    }
  }, [widgetId]);

  // Fetch date-specific work availability when date is selected
  // Always fetch work hours for any service type to show correct morning/afternoon slots
  useEffect(() => {
    if (formData.date) {
      const fetchDateAvailability = async () => {
        setLoadingAvailability(true);
        try {
          const dateStr = formData.date.toISOString().split('T')[0];
          const response = await widgetApi.getWorkAvailability(widgetId, dateStr);
          setDateAvailability(response.data);
        } catch (error) {
          console.error('Error fetching date availability:', error);
          setDateAvailability(null);
        } finally {
          setLoadingAvailability(false);
        }
      };
      fetchDateAvailability();
    }
  }, [formData.date, widgetId]);

  // Fetch availability when date changes
  useEffect(() => {
    if (formData.date && formData.service_ids.length > 0) {
      const fetchAvailability = async () => {
        try {
          const dateStr = formData.date.toISOString().split('T')[0];
          const response = await widgetApi.getPunctualAvailability(widgetId, dateStr, formData.service_ids[0]);
          setAvailability(response.data.time_slots || []);
        } catch (error) {
          console.error('Error fetching availability:', error);
        }
      };
      fetchAvailability();
    }
  }, [formData.date, formData.service_ids, widgetId]);

  // Update selected services when service_ids changes
  useEffect(() => {
    if (formData.service_ids.length > 0 && config?.services) {
      const services = config.services.filter(s => formData.service_ids.includes(s.id));
      setSelectedServices(services);
      
      // Fetch collaborators for all selected services
      const fetchCollaborators = async () => {
        try {
          const dateStr = formData.date
            ? formData.date.toISOString().split('T')[0]
            : undefined;
          // Translate slot_type/period into start/end for collab-availability check
          let start_time, end_time;
          if (formData.slot_type === 'half_day' && formData.slot_period === 'morning') {
            start_time = '08:00'; end_time = '12:00';
          } else if (formData.slot_type === 'half_day' && formData.slot_period === 'afternoon') {
            start_time = '14:00'; end_time = '18:00';
          } else if (formData.slot_type === 'full_day') {
            start_time = '08:00'; end_time = '18:00';
          }
          // 1) date-aware call → only available collabs
          const response = await widgetApi.getPunctualCollaborators(
            widgetId,
            formData.service_ids[0],
            {
              date: dateStr,
              start_time,
              end_time,
              slot_period: formData.slot_period || undefined,
            }
          );
          setFilteredCollaborators(response.data);
          // 2) date-less call → all collabs eligible for that service, used to
          // show busy ones as greyed entries (so the client knows they exist).
          if (dateStr) {
            try {
              const fullResp = await widgetApi.getPunctualCollaborators(
                widgetId,
                formData.service_ids[0],
                {}
              );
              setServiceCollaborators(fullResp.data);
            } catch {
              setServiceCollaborators(response.data);
            }
          } else {
            setServiceCollaborators(response.data);
          }
        } catch (error) {
          console.error('Error fetching collaborators:', error);
          setFilteredCollaborators(config?.collaborators || []);
          setServiceCollaborators(config?.collaborators || []);
        }
      };
      fetchCollaborators();
    } else {
      setSelectedServices([]);
      setFilteredCollaborators(config?.collaborators || []);
      setServiceCollaborators(config?.collaborators || []);
    }
  }, [formData.service_ids, formData.date, formData.slot_type, formData.slot_period, config?.services, config?.collaborators, widgetId]);

  // Calculate price based on widget-level pricing mode + selected services and slot type
  const calculatePrice = () => {
    const hours = formData.slot_type === 'full_day' ? 7 : (formData.slot_type === 'half_day' ? 3.5 : 0);

    // Mode 1: Prix fixe par créneau horaire (widget level)
    if (widgetPricingMode === 'per_slot_type') {
      const st = slotTypes.find(s => (s.code || s.id) === formData.slot_type);
      if (st) {
        return getSlotTypePrice(st);
      }
      return 0;
    }

    // Mode 2: Prix par service (widget level)
    if (widgetPricingMode === 'per_service') {
      return selectedServices.reduce((sum, s) => sum + (widgetServicePrices[s.id] || 0), 0);
    }

    // Mode 3 (default): Taux horaire — service-level pricing
    let totalPrice = 0;
    selectedServices.forEach(service => {
      const pricingMode = service.pricing_mode || service.price_type || 'hourly';
      
      if (pricingMode === 'fixed') {
        // Add fixed price
        totalPrice += service.fixed_price || 0;
      } else if (pricingMode === 'hourly') {
        // Calculate hourly price
        const rate = service.price_per_hour || defaultHourlyRate;
        totalPrice += hours * rate;
      }
      // 'info_only' services don't add to the price
    });
    
    // If no priced services selected, use default hourly rate × hours
    if (totalPrice === 0 && hours > 0) {
      totalPrice = hours * defaultHourlyRate;
    }
    
    return totalPrice;
  };

  // Get hours for selected slot type
  const getSlotHours = () => {
    const st = slotTypes.find(s => (s.code || s.id) === formData.slot_type);
    if (st?.duration_hours) return st.duration_hours;
    return formData.slot_type === 'full_day' ? 7 : (formData.slot_type === 'half_day' ? 3.5 : 0);
  };

  // ─── Avance immédiate crédit d'impôt (50% URSSAF) ───
  // Display the "Reste à votre charge" as the headline price when ALL
  // selected services are flagged tax_credit_eligible. The original (base)
  // price is shown smaller below, struck-through, with the disclaimer.
  const _isTaxCreditEligible = (() => {
    if (selectedServices.length === 0) return false;
    return selectedServices.every((s) => s.tax_credit_eligible !== false);
  })();

  // Tag displayed next to each service in the selection list (step 0).
  // When the service is tax-credit eligible, show a compact "Reste à charge"
  // headline + base price below, mirroring the final summary look.
  const ServicePriceTag = ({ service, widgetPricingMode, widgetServicePrice, pricingMode, primaryColor, textColor }) => {
    const eligible = service?.tax_credit_eligible !== false;
    // Resolve the base price for this individual service
    let base = null;
    let label = null;
    if (widgetPricingMode === 'per_service' && widgetServicePrice != null && widgetServicePrice > 0) {
      base = widgetServicePrice;
    } else if (widgetPricingMode === 'per_slot_type') {
      // Price varies per slot — keep the lightweight tag
      return (
        <Badge variant="outline" style={{ color: primaryColor, borderColor: primaryColor }} className="shrink-0">
          Tarif au créneau
        </Badge>
      );
    } else if (widgetPricingMode === 'hourly' && pricingMode === 'fixed' && service.fixed_price) {
      base = service.fixed_price;
    } else if (widgetPricingMode === 'hourly' && pricingMode === 'hourly' && service.price_per_hour) {
      base = service.price_per_hour;
      label = '/h';
    } else if (widgetPricingMode === 'hourly' && pricingMode === 'info_only') {
      return <Badge variant="secondary" className="text-xs shrink-0">Info</Badge>;
    }
    if (!base || base <= 0) return null;
    if (!eligible) {
      return (
        <Badge variant="outline" style={{ color: primaryColor, borderColor: primaryColor }} className="shrink-0">
          {base.toFixed(0)}€{label || ''}
        </Badge>
      );
    }
    return (
      <div className="text-right shrink-0" data-testid={`service-price-tag-${service.id}`}>
        <p className="text-[10px] uppercase tracking-wide font-medium leading-none" style={{ color: `${textColor}80` }}>
          Reste à charge
        </p>
        <p className="text-lg font-heading font-semibold leading-tight mt-0.5" style={{ color: primaryColor }}>
          {(base * 0.5).toFixed(2)}€{label || ''}
        </p>
        <p className="text-[11px] leading-none mt-0.5" style={{ color: `${textColor}80` }}>
          au lieu de <span className="font-semibold">{base.toFixed(2)}€{label || ''}</span>
        </p>
      </div>
    );
  };

  // Render a price block that swaps base ↔ remaining when eligible.
  //   size = 'lg' (2xl text on headline) | 'md' (xl text)
  //   align = 'right' | 'left'
  const PriceBlock = ({ price, size = 'lg', align = 'right', suffix = null }) => {
    if (!price || price <= 0) return null;
    const big = size === 'lg' ? 'text-2xl' : 'text-xl';
    const remaining = price * 0.5;
    if (_isTaxCreditEligible) {
      return (
        <div className={align === 'right' ? 'text-right' : ''} data-testid="tax-credit-price">
          <p className="text-[11px] uppercase tracking-wide font-medium" style={{ color: `${textColor}90` }}>
            Reste à votre charge
          </p>
          <p className={`${big} font-heading font-semibold leading-tight mt-0.5`} style={{ color: primaryColor }}>
            {remaining.toFixed(2)}€
            {suffix}
          </p>
          <p className="text-sm mt-1" style={{ color: `${textColor}80` }}>
            Tarif de base : <span className="font-bold text-base">{price.toFixed(2)}€</span>
          </p>
          <p className="text-[11px] italic" style={{ color: `${textColor}60` }}>
            (Après déduction d'avance immédiate de crédit d'impôts)
          </p>
        </div>
      );
    }
    return (
      <div className={align === 'right' ? 'text-right' : ''}>
        <p className={`${big} font-heading font-semibold`} style={{ color: primaryColor }}>
          {price.toFixed(2)}€
          {suffix}
        </p>
      </div>
    );
  };

  // Slot type classification — used to decide whether to show period selector,
  // auto-set slot_period, and build the booking summary.
  const currentSlotType = slotTypes.find(s => (s.code || s.id) === formData.slot_type);
  const _stCode = (currentSlotType?.code || formData.slot_type || '').toString().toLowerCase();
  const _stName = (currentSlotType?.name || '').toLowerCase();
  const _isFullDaySelection = (
    !(
      /(^|[^a-z])demi([^a-z]|$)|half|matin|apr[eè]s|aprem/.test(_stCode)
      || /demi|half|matin|apr[eè]s|aprem/.test(_stName)
    )
    && (
      _stCode === 'full_day' ||
      /journee|complete|full/.test(_stCode) ||
      /journ[eé]e|complete|full/.test(_stName) ||
      (currentSlotType?.duration_hours && currentSlotType.duration_hours >= 6)
    )
  );
  const _isMorningSelection = !_isFullDaySelection && (/^matin$|morning/.test(_stCode) || /^matin\b/.test(_stName));
  const _isAfternoonSelection = !_isFullDaySelection && (/apres|aprem|afternoon/.test(_stCode) || /apr[eè]s|aprem|afternoon/.test(_stName));
  // Generic half-day (no specific period) → user must still pick matin/aprem
  const _isAmbiguousHalfDay = !_isFullDaySelection && !_isMorningSelection && !_isAfternoonSelection && currentSlotType;

  // Decide if a given slot type is available on the currently selected date
  // based on dateAvailability returned by /widget/punctual/.../work-availability.
  // Rule:
  //  - 'full_day' code OR duration >= 6h  → needs full_day_available
  //  - 'half_day' code OR duration <= 5h  → needs morning OR afternoon
  // Returns { available: bool, reason: string }
  const getClosureReason = (date) => {
    if (!closures || closures.length === 0) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const iso = `${y}-${m}-${d}`;
    const hit = closures.find((c) => {
      const start = c.start_date;
      const end = c.end_date || start;
      if (!start) return false;
      return iso >= start && iso <= end;
    });
    if (!hit) return null;
    if (hit.type === 'french_holiday') return hit.reason || 'Jour férié';
    return hit.reason || 'Fermeture';
  };
  const isDateClosed = (date) => getClosureReason(date) !== null;

  const slotTypeAvailability = (st) => {
    if (!dateAvailability) return { available: true, reason: '' };
    if (!dateAvailability.is_working_day) {
      return { available: false, reason: 'Jour fermé' };
    }
    const code = st.code || st.id;
    const isFullDay = code === 'full_day' || (st.duration_hours && st.duration_hours >= 6);
    if (isFullDay) {
      if (dateAvailability.full_day_available) return { available: true, reason: '' };
      return { available: false, reason: 'Journée complète non proposée ce jour' };
    }
    // half-day-like
    const hasAnyHalf = dateAvailability.morning_available || dateAvailability.afternoon_available;
    if (hasAnyHalf) return { available: true, reason: '' };
    return { available: false, reason: 'Aucune demi-journée disponible ce jour' };
  };

  // Check if a service is info only (no price)
  const isInfoOnly = (service) => {
    const mode = service.pricing_mode || service.price_type;
    return mode === 'info_only';
  };

  // Toggle service selection
  const toggleService = (serviceId) => {
    const newIds = formData.service_ids.includes(serviceId)
      ? formData.service_ids.filter(id => id !== serviceId)
      : [...formData.service_ids, serviceId];
    setFormData({ ...formData, service_ids: newIds });
  };

  const nextStep = () => {
    if (currentStep === 0 && formData.service_ids.length === 0) {
      toast.error('Veuillez sélectionner au moins un service');
      return;
    }
    // Get hide_duration_choice from widget config
    const hideDurationChoice = config?.widget?.hide_duration_choice === true;
    
    if (currentStep === 1 && !formData.date) {
      toast.error('Veuillez sélectionner une date');
      return;
    }
    // Only validate slot_type if duration choice is visible
    if (currentStep === 1 && !hideDurationChoice && !formData.slot_type) {
      toast.error('Veuillez sélectionner un type de créneau');
      return;
    }
    if (currentStep === 1 && _isAmbiguousHalfDay && !formData.slot_period) {
      toast.error('Veuillez choisir Matin ou Après-midi');
      return;
    }
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Check if selected services require payment (have a price > 0)
  const requiresPayment = () => {
    const price = calculatePrice();
    return price > 0 && config?.widget?.booking_mode === 'direct';
  };

  // Handle payment initiation
  const handlePayment = async () => {
    if (!formData.client_name || !formData.client_email) {
      toast.error('Veuillez remplir votre nom et email');
      return;
    }

    setIsSubmitting(true);
    try {
      const paymentData = {
        widget_id: widgetId,
        booking_type: 'punctual',
        service_ids: formData.service_ids,
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone || null,
        origin_url: window.location.origin,
        date: formData.date?.toISOString().split('T')[0],
        slot_type: formData.slot_type,
        slot_period: formData.slot_period,
        notes: formData.notes || '',
        custom_answers: formData.custom_answers || {},
        preferred_collaborator_id: formData.preferred_collaborator_id || null
      };

      const response = await widgetApi.createPayment(widgetId, paymentData);
      
      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Erreur lors de la création du paiement');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.client_name || !formData.client_email) {
      toast.error('Veuillez remplir votre nom et email');
      return;
    }

    setIsSubmitting(true);
    try {
      // Calculate start_time based on slot type & period.
      // Use the day's work hours periods when available, fall back to defaults.
      let startTime = '08:00';
      if (_isFullDaySelection) {
        startTime = (dateAvailability?.full_day?.start) || (dateAvailability?.morning?.start) || '08:00';
      } else if (_isAfternoonSelection || formData.slot_period === 'afternoon') {
        startTime = (dateAvailability?.afternoon?.start) || '14:00';
      } else {
        startTime = (dateAvailability?.morning?.start) || '08:00';
      }
      
      const durationMinutes = getSlotHours() * 60; // Convert hours to minutes
      
      const submitData = {
        service_ids: formData.service_ids,
        service_id: formData.service_ids[0], // Required for backend
        widget_id: widgetId,
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone || '',
        date: formData.date.toISOString().split('T')[0],
        start_time: startTime,
        slot_period: formData.slot_period || null,
        slot_type: formData.slot_type || null,
        duration_minutes: durationMinutes,
        notes: formData.notes || '',
        custom_answers: formData.custom_answers || {},
        total_price: calculatePrice(),
        client_address: formData.client_address || null,
        client_coords: formData.client_coords || null,
        // Lock booking to the selected collaborator (when set) so the server
        // can refuse double-bookings and the planning back-office shows the
        // booking under the right swimlane.
        preferred_collaborator_id: formData.preferred_collaborator_id || null,
        collaborator_id: formData.preferred_collaborator_id || null,
        // Recurrence fields
        is_recurring: formData.is_recurring,
        recurrence_type: formData.is_recurring ? formData.recurrence_type : null,
        recurrence_days: formData.is_recurring ? formData.recurrence_days : [],
        recurrence_interval: formData.is_recurring ? formData.recurrence_interval : null,
        recurrence_end_type: formData.is_recurring ? formData.recurrence_end_type : null,
        recurrence_count: formData.is_recurring ? formData.recurrence_count : null,
        recurrence_end_date: formData.is_recurring ? formData.recurrence_end_date : null
      };
      
      // Use different endpoint for recurring vs single bookings
      let response;
      if (formData.is_recurring && formData.recurrence_type) {
        response = await widgetApi.createRecurringBooking(widgetId, submitData);
      } else {
        response = await widgetApi.createPunctualBooking(widgetId, submitData);
      }
      setBookingResult(response.data);
      setSubmitted(true);
      toast.success(formData.is_recurring ? 'Réservations récurrentes créées!' : 'Réservation envoyée!');
    } catch (error) {
      console.error('Booking error:', error);
      const detail = error?.response?.data?.detail;
      // Recurring booking returns a structured detail with conflicts
      if (detail && typeof detail === 'object' && Array.isArray(detail.conflicts)) {
        const dates = detail.conflicts.map((c) => c.date).join(', ');
        toast.error(`${detail.message || 'Conflit de planning'} : ${dates}`);
      } else {
        const msg = typeof detail === 'string' ? detail : 'Erreur lors de la réservation';
        toast.error(msg);
        // Si le serveur refuse à cause du collaborateur préféré indisponible,
        // on remet à zéro la préférence + on renvoie l'utilisateur à l'étape 0
        // pour lui permettre de changer d'intervenant.
        if (typeof msg === 'string' && /collaborateur.*(disponible|occupé)/i.test(msg)) {
          setFormData((prev) => ({ ...prev, preferred_collaborator_id: '' }));
          setCurrentStep(0);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle quote request submission (Aménagement path)
  const handleQuoteSubmit = async () => {
    if (!quoteData.client_name || !quoteData.client_email) {
      toast.error('Veuillez remplir votre nom et email');
      return;
    }
    if (!quoteData.appointment_date || !quoteData.appointment_time) {
      toast.error('Veuillez sélectionner une date et un créneau');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        company_id: config?.company?.id,
        widget_id: widgetId,
        client_name: quoteData.client_name,
        client_email: quoteData.client_email,
        client_phone: quoteData.client_phone || '',
        client_address: quoteData.client_address || '',
        client_coords: quoteData.client_coords || null,
        selected_services: quoteData.selected_services,
        appointment_date: quoteData.appointment_date.toISOString().split('T')[0],
        appointment_time: quoteData.appointment_time,
        request_description: quoteData.request_description || '',
        custom_answers: quoteData.custom_answers || {},
      };
      
      await widgetApi.createQuoteRequest(widgetId, submitData);
      setQuoteSubmitted(true);
      toast.success('Demande de devis envoyée!');
    } catch (error) {
      console.error('Quote request error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la demande');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Navigation for quote steps (Aménagement path)
  const nextQuoteStep = () => {
    if (quoteStep === 0 && quoteData.selected_services.length === 0) {
      toast.error('Veuillez sélectionner au moins un service');
      return;
    }
    if (quoteStep === 1 && (!quoteData.appointment_date || !quoteData.appointment_time)) {
      toast.error('Veuillez sélectionner une date et un créneau de RDV');
      return;
    }
    if (quoteStep < QUOTE_STEPS.length - 1) {
      setQuoteStep(quoteStep + 1);
    }
  };

  const prevQuoteStep = () => {
    if (quoteStep > 0) {
      setQuoteStep(quoteStep - 1);
    } else {
      // Go back to choice screen
      setServiceChoice(null);
    }
  };

  // Toggle service for quote request
  const toggleQuoteService = (serviceId) => {
    const newIds = quoteData.selected_services.includes(serviceId)
      ? quoteData.selected_services.filter(id => id !== serviceId)
      : [...quoteData.selected_services, serviceId];
    setQuoteData({ ...quoteData, selected_services: newIds });
  };

  // Get services for Aménagement family
  const getAmenagementServices = () => {
    if (!config?.services) return [];
    return config.services.filter(s => 
      s.family_name?.toLowerCase().includes('aménagement') ||
      s.family_id
    );
  };

  // Handle redirect to subscription widget for Entretien
  const handleEntretienChoice = () => {
    // Rediriger vers le widget abonnement de l'entreprise
    if (config?.subscription_widget_id) {
      // Redirection vers le widget abonnement
      const baseUrl = window.location.origin;
      const embedParam = isEmbedded ? '?embed=true' : '';
      window.location.href = `${baseUrl}/widget/subscription/${config.subscription_widget_id}${embedParam}`;
    } else if (config?.company?.id) {
      // Chercher le widget abonnement par company_id
      const baseUrl = window.location.origin;
      const embedParam = isEmbedded ? '?embed=true' : '';
      window.location.href = `${baseUrl}/widget/recurring/${config.company.id}${embedParam}`;
    } else {
      // Fallback: continuer avec le flux normal (ne devrait pas arriver)
      setServiceChoice('entretien');
    }
  };

  // Transparent background for iframe embedding
  const containerStyle = { backgroundColor: 'transparent', color: textColor };
  const primaryBtnStyle = { backgroundColor: primaryColor, color: '#ffffff' };
  const secondaryBtnStyle = { backgroundColor: secondaryColor, color: '#ffffff' };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" style={containerStyle}>
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-16 h-16 rounded-2xl mb-4" style={{ backgroundColor: `${primaryColor}20` }} />
          <p style={{ color: `${textColor}80` }}>Chargement...</p>
        </div>
      </div>
    );
  }

  if (loadError || !config || !config.widget) {
    const isNotFound = loadError === 'not_found' || !config;
    return (
      <div className="flex items-center justify-center p-4" style={containerStyle}>
        <Card className="max-w-md w-full" data-testid="widget-load-error">
          <CardContent className="p-8 text-center">
            <Leaf className="w-12 h-12 mx-auto mb-4" style={{ color: `${textColor}60` }} />
            <p className="text-lg font-medium">{isNotFound ? 'Service indisponible' : 'Erreur de connexion'}</p>
            <p className="mt-2 text-sm" style={{ color: `${textColor}70` }}>
              {isNotFound
                ? "Ce widget de réservation n'est pas accessible. Merci de contacter le prestataire."
                : "Impossible de joindre le serveur. Réessayez dans quelques instants."}
            </p>
            {!isNotFound && (
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: primaryColor }}
              >
                Réessayer
              </button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get display start time based on slot selection and work hours
  const getDisplayStartTime = () => {
    if (formData.slot_type === 'full_day') {
      // Use work hours data if available
      if (dateAvailability?.morning_display && dateAvailability?.afternoon_display) {
        const morningStart = dateAvailability.morning_display.split(' - ')[0];
        const afternoonEnd = dateAvailability.afternoon_display.split(' - ')[1];
        return `${morningStart} - ${afternoonEnd}`;
      }
      return '8h - 15h';
    }
    if (formData.slot_type === 'half_day') {
      if (formData.slot_period === 'afternoon') {
        return dateAvailability?.afternoon_display || '14h - 17h30';
      }
      return dateAvailability?.morning_display || '8h - 11h30';
    }
    return '';
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center p-4" style={containerStyle}>
        <Card className="max-w-md w-full shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-heading font-semibold">
              {bookingResult?.status === 'confirmed' ? 'Réservation Confirmée!' : 'Demande Envoyée!'}
            </h2>
            <p className="mt-2" style={{ color: `${textColor}70` }}>
              {bookingResult?.status === 'confirmed' 
                ? `Votre réservation chez ${config.company.name} est confirmée.`
                : `Votre demande a été envoyée à ${config.company.name}. Vous recevrez une confirmation par email.`
              }
            </p>
            <div className="mt-6 p-4 rounded-xl text-left" style={{ backgroundColor: `${primaryColor}10` }}>
              <p className="text-sm" style={{ color: `${textColor}70` }}>Détails de la réservation</p>
              <p className="font-medium mt-1">{selectedServices.map(s => s.name).join(', ') || bookingResult?.service_name}</p>
              <p className="text-sm mt-1">
                {formData.date?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              <p className="text-sm">{getDisplayStartTime()}</p>
              <div className="mt-2">
                <PriceBlock price={calculatePrice()} align="left" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Quote request submitted (Aménagement path)
  if (quoteSubmitted) {
    return (
      <div className="flex items-center justify-center p-4" style={containerStyle}>
        <Card className="max-w-md w-full shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-heading font-semibold">Demande de Devis Envoyée!</h2>
            <p className="mt-2" style={{ color: `${textColor}70` }}>
              Votre demande de RDV a été envoyée à {config.company.name}. Un commercial vous contactera très prochainement.
            </p>
            <div className="mt-6 p-4 rounded-xl text-left" style={{ backgroundColor: `${primaryColor}10` }}>
              <p className="text-sm" style={{ color: `${textColor}70` }}>Détails du RDV</p>
              <p className="font-medium mt-1">
                {quoteData.appointment_date?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              <p className="text-sm">à {quoteData.appointment_time}</p>
              <p className="text-sm mt-2" style={{ color: `${textColor}70` }}>
                Services: {config.services?.filter(s => quoteData.selected_services.includes(s.id)).map(s => s.name).join(', ')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Choice screen: Récurrent vs Ponctuel
  if (serviceChoice === null) {
    return (
      <div className="p-4 md:p-6" style={containerStyle}>
        <div className="max-w-lg mx-auto">
          {/* Header */}
          {!isEmbedded && (
            <div className="text-center mb-8">
              {config.company.logo_url && config.widget.show_logo !== false ? (
                <div
                  className="w-28 h-28 mx-auto mb-4 rounded-full bg-white flex items-center justify-center overflow-hidden p-2.5 border-2 transition-all duration-300 hover:scale-105"
                  style={{ borderColor: primaryColor, boxShadow: `0 4px 22px ${primaryColor}33` }}
                >
                  <img 
                    src={getImageUrl(config.company.logo_url)}
                    alt={config.company.name}
                    className="max-w-full max-h-full object-contain" loading="lazy" decoding="async"
                  />
                </div>
              ) : config.widget.show_logo !== false && (
                <div 
                  className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
                  style={{ backgroundColor: `${primaryColor}20` }}
                >
                  <Leaf className="w-7 h-7" style={{ color: primaryColor }} />
                </div>
              )}
              <h1 className="text-xl font-heading font-semibold">{config.company.name}</h1>
            </div>
          )}

          <Card className="shadow-lg border-0 overflow-hidden">
            <CardContent className="p-6">
              <h2 className="text-xl font-heading font-semibold text-center mb-2">
                Quelle est la nature de votre demande ?
              </h2>
              <p className="text-center text-sm mb-6" style={{ color: `${textColor}70` }}>
                Choisissez le type de prestation souhaité
              </p>

              <div className="grid gap-4">
                {/* Option Récurrent - Abonnement */}
                <button
                  onClick={handleEntretienChoice}
                  className="flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all hover:shadow-md group"
                  style={{ borderColor: '#22C55E', backgroundColor: '#F0FDF4' }}
                >
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-green-100">
                    <Repeat className="w-7 h-7 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-lg">Entretien Récurrent</p>
                    <p className="text-sm text-muted-foreground">
                      Abonnement mensuel, entretien régulier de votre jardin
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-green-600 group-hover:translate-x-1 transition-transform" />
                </button>

                {/* Option Ponctuel - va vers la sélection de services */}
                <button
                  onClick={() => setServiceChoice('ponctuel')}
                  className="flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all hover:shadow-md group"
                  style={{ borderColor: '#3B82F6', backgroundColor: '#EFF6FF' }}
                >
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-blue-100">
                    <Calendar className="w-7 h-7 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-lg">Intervention Ponctuelle</p>
                    <p className="text-sm text-muted-foreground">
                      Une intervention unique : entretien ou aménagement
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-blue-600 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Ponctuel path: Service selection - determines if Entretien or Aménagement
  if (serviceChoice === 'ponctuel') {
    // Check selected services to determine path
    const selectedServiceObjects = config.services?.filter(s => formData.service_ids.includes(s.id)) || [];
    const hasAmenagementService = selectedServiceObjects.some(s => 
      s.family_name?.toLowerCase().includes('aménagement') || 
      s.family_id || 
      s.category?.toLowerCase().includes('aménagement')
    );

    return (
      <div className="p-4 md:p-6" style={containerStyle}>
        <div className="max-w-lg mx-auto">
          {/* Header */}
          {!isEmbedded && (
            <div className="text-center mb-6">
              {config.company.logo_url && config.widget.show_logo !== false ? (
                <div
                  className="w-24 h-24 mx-auto mb-3 rounded-full bg-white flex items-center justify-center overflow-hidden p-2 border-2 transition-all duration-300 hover:scale-105"
                  style={{ borderColor: primaryColor, boxShadow: `0 4px 20px ${primaryColor}33` }}
                >
                  <img 
                    src={getImageUrl(config.company.logo_url)}
                    alt={config.company.name}
                    className="max-w-full max-h-full object-contain" loading="lazy" decoding="async"
                  />
                </div>
              ) : null}
              <p className="text-sm font-medium" style={{ color: primaryColor }}>Intervention Ponctuelle</p>
            </div>
          )}

          <Card className="shadow-lg border-0 overflow-hidden">
            <CardContent className="p-6">
              <h2 className="text-xl font-heading font-semibold text-center mb-2">
                Sélectionnez vos services
              </h2>
              <p className="text-center text-sm mb-6" style={{ color: `${textColor}70` }}>
                Choisissez un ou plusieurs services
              </p>

              <div className="grid gap-3 max-h-80 overflow-y-auto pr-2 mb-6">
                {(config.services || []).map((service) => {
                  const servicePricingMode = service.pricing_mode || service.price_type || 'hourly';
                  const widgetServicePrice = widgetPricingMode === 'per_service' ? widgetServicePrices[service.id] : undefined;
                  return (
                  <div
                    key={service.id}
                    onClick={() => toggleService(service.id)}
                    className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all border-2 ${
                      formData.service_ids.includes(service.id)
                        ? 'shadow-md'
                        : 'border-transparent hover:border-muted-foreground/20'
                    }`}
                    style={{
                      backgroundColor: formData.service_ids.includes(service.id) ? `${primaryColor}10` : undefined,
                      borderColor: formData.service_ids.includes(service.id) ? primaryColor : undefined
                    }}
                  >
                    <div 
                      className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        formData.service_ids.includes(service.id) ? 'text-white' : 'border-2 border-muted-foreground/30'
                      }`}
                      style={{ backgroundColor: formData.service_ids.includes(service.id) ? primaryColor : undefined }}
                    >
                      {formData.service_ids.includes(service.id) && <Check className="w-4 h-4" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{service.name}</p>
                      {service.description && (
                        <p className="text-xs text-muted-foreground">{service.description}</p>
                      )}
                      {/* Badge pour indiquer le type */}
                      {(service.family_name?.toLowerCase().includes('aménagement') || service.category?.toLowerCase().includes('aménagement')) && (
                        <Badge className="bg-blue-100 text-blue-700 text-[10px] mt-1">Aménagement</Badge>
                      )}
                    </div>
                    {/* Widget-level pricing mode badge */}
                    <ServicePriceTag
                      service={service}
                      widgetPricingMode={widgetPricingMode}
                      widgetServicePrice={widgetServicePrice}
                      pricingMode={servicePricingMode}
                      primaryColor={primaryColor}
                      textColor={textColor}
                    />
                  </div>
                  );
                })}
              </div>

              {/* Navigation */}
              <div className="flex justify-between pt-4 border-t">
                <Button variant="ghost" onClick={() => setServiceChoice(null)}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Retour
                </Button>
                <Button 
                  onClick={() => {
                    if (formData.service_ids.length === 0) {
                      toast.error('Veuillez sélectionner au moins un service');
                      return;
                    }
                    // Vérifier si les services sélectionnés sont de type Aménagement
                    const selectedServices = config.services?.filter(s => formData.service_ids.includes(s.id)) || [];
                    const isAmenagement = selectedServices.some(s => 
                      s.family_name?.toLowerCase().includes('aménagement') || 
                      s.family_id || 
                      s.category?.toLowerCase().includes('aménagement')
                    );
                    
                    if (isAmenagement) {
                      // Basculer vers le parcours devis
                      setQuoteData({ ...quoteData, selected_services: formData.service_ids });
                      setServiceChoice('amenagement');
                      setQuoteStep(1); // Aller directement à l'étape RDV
                    } else {
                      // Continuer avec le parcours ponctuel classique
                      setServiceChoice('entretien');
                      setCurrentStep(1); // Aller à l'étape date/heure
                    }
                  }}
                  style={primaryBtnStyle}
                >
                  Continuer
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Quote request flow (Aménagement path)
  if (serviceChoice === 'amenagement') {
    const amenagementServices = getAmenagementServices().length > 0 
      ? getAmenagementServices() 
      : config.services || [];
    const activeQuoteSteps = QUOTE_STEPS;

    return (
      <div className="p-4 md:p-6" style={containerStyle}>
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          {!isEmbedded && (
            <div className="text-center mb-6">
              {config.company.logo_url && config.widget.show_logo !== false ? (
                <div
                  className="w-24 h-24 mx-auto mb-3 rounded-full bg-white flex items-center justify-center overflow-hidden p-2 border-2 transition-all duration-300 hover:scale-105"
                  style={{ borderColor: primaryColor, boxShadow: `0 4px 20px ${primaryColor}33` }}
                >
                  <img 
                    src={getImageUrl(config.company.logo_url)}
                    alt={config.company.name}
                    className="max-w-full max-h-full object-contain" loading="lazy" decoding="async"
                  />
                </div>
              ) : null}
              <p className="text-sm font-medium" style={{ color: primaryColor }}>Demande de Devis Aménagement</p>
            </div>
          )}

          {/* Progress steps */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {activeQuoteSteps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                    index <= quoteStep ? 'text-white' : 'text-muted-foreground bg-muted'
                  }`}
                  style={{ backgroundColor: index <= quoteStep ? primaryColor : undefined }}
                >
                  {index < quoteStep ? <Check className="w-4 h-4" /> : index + 1}
                </div>
                {index < activeQuoteSteps.length - 1 && (
                  <div 
                    className={`w-12 h-1 mx-1 rounded ${index < quoteStep ? '' : 'bg-muted'}`}
                    style={{ backgroundColor: index < quoteStep ? primaryColor : undefined }}
                  />
                )}
              </div>
            ))}
          </div>

          <Card className="shadow-lg border-0">
            <CardContent className="p-6">
              {/* Step 0: Services */}
              {quoteStep === 0 && (
                <div className="space-y-4">
                  <div className="text-center mb-4">
                    <h2 className="text-xl font-heading font-semibold">Sélectionnez vos services</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Choisissez les services d'aménagement souhaités
                    </p>
                  </div>
                  <div className="grid gap-3 max-h-80 overflow-y-auto pr-2">
                    {amenagementServices.map((service) => (
                      <div
                        key={service.id}
                        onClick={() => toggleQuoteService(service.id)}
                        className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all border-2 ${
                          quoteData.selected_services.includes(service.id)
                            ? 'shadow-md'
                            : 'border-transparent hover:border-muted-foreground/20'
                        }`}
                        style={{
                          backgroundColor: quoteData.selected_services.includes(service.id) ? `${primaryColor}10` : undefined,
                          borderColor: quoteData.selected_services.includes(service.id) ? primaryColor : undefined
                        }}
                      >
                        <div 
                          className={`w-5 h-5 rounded-full flex items-center justify-center ${
                            quoteData.selected_services.includes(service.id) ? 'text-white' : 'border-2 border-muted-foreground/30'
                          }`}
                          style={{ backgroundColor: quoteData.selected_services.includes(service.id) ? primaryColor : undefined }}
                        >
                          {quoteData.selected_services.includes(service.id) && <Check className="w-3 h-3" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{service.name}</p>
                          {service.description && (
                            <p className="text-xs text-muted-foreground">{service.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 1: RDV Date/Time */}
              {quoteStep === 1 && (
                <div className="space-y-4">
                  <div className="text-center mb-4">
                    <h2 className="text-xl font-heading font-semibold">Choisir un RDV</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Sélectionnez une date pour un RDV avec notre commercial
                      {commercialInfo && <span className="font-medium"> ({commercialInfo.name})</span>}
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Date du RDV</Label>
                      <CalendarComponent
                        mode="single"
                        selected={quoteData.appointment_date}
                        onSelect={(date) => setQuoteData({ ...quoteData, appointment_date: date, appointment_time: '' })}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const maxDate = new Date();
                          maxDate.setDate(maxDate.getDate() + 90); // 90 jours max
                          if (isDateClosed(date)) return true;
                          return date < today || date > maxDate || date.getDay() === 0 || date.getDay() === 6;
                        }}
                        components={{
                          DayContent: ({ date }) => {
                            const closureReason = getClosureReason(date);
                            return (
                              <div className="flex flex-col items-center justify-center h-8 w-8" title={closureReason || undefined}>
                                <span className="leading-none">{date.getDate()}</span>
                                {closureReason && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-0.5" />
                                )}
                              </div>
                            );
                          },
                        }}
                        className="rounded-xl border"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Réservation possible jusqu'à 90 jours à l'avance
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Créneau horaire</Label>
                      {!quoteData.appointment_date ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">
                          Sélectionnez d'abord une date
                        </p>
                      ) : commercialSlots.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">
                          Aucun créneau disponible pour cette date
                        </p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {commercialSlots.map((slot) => (
                            <button
                              key={slot.time}
                              onClick={() => setQuoteData({ ...quoteData, appointment_time: slot.time })}
                              className={`p-3 rounded-xl text-sm font-medium border-2 transition-all ${
                                quoteData.appointment_time === slot.time
                                  ? 'text-white shadow-md'
                                  : 'border-muted hover:border-muted-foreground/30'
                              }`}
                              style={{
                                backgroundColor: quoteData.appointment_time === slot.time ? primaryColor : undefined,
                                borderColor: quoteData.appointment_time === slot.time ? primaryColor : undefined
                              }}
                            >
                              {slot.display}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Contact Info */}
              {quoteStep === 2 && (
                <div className="space-y-4">
                  <div className="text-center mb-4">
                    <h2 className="text-xl font-heading font-semibold">Vos coordonnées</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Pour que notre commercial puisse vous contacter
                    </p>
                  </div>
                  <div className="grid gap-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nom complet *</Label>
                        <Input
                          value={quoteData.client_name}
                          onChange={(e) => setQuoteData({ ...quoteData, client_name: e.target.value })}
                          placeholder="Jean Dupont"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Téléphone</Label>
                        <Input
                          value={quoteData.client_phone}
                          onChange={(e) => setQuoteData({ ...quoteData, client_phone: e.target.value })}
                          placeholder="06 12 34 56 78"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={quoteData.client_email}
                        onChange={(e) => setQuoteData({ ...quoteData, client_email: e.target.value })}
                        placeholder="jean@email.fr"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Adresse du projet (lieu du RDV)</Label>
                      <AddressAutocomplete
                        value={quoteData.client_address}
                        onChange={(v) => setQuoteData({ ...quoteData, client_address: v })}
                        onSelect={(sel) => setQuoteData(prev => ({
                          ...prev,
                          client_address: sel.label,
                          client_coords: sel.coords || null,
                        }))}
                        placeholder="Commencez à taper votre adresse…"
                        data-testid="punctual-quote-address-autocomplete"
                        savedAddresses={savedAddresses}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description du projet (optionnel)</Label>
                      <Textarea
                        value={quoteData.request_description}
                        onChange={(e) => setQuoteData({ ...quoteData, request_description: e.target.value })}
                        placeholder="Décrivez brièvement votre projet d'aménagement..."
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Confirmation */}
              {quoteStep === 3 && (
                <div className="space-y-4">
                  <div className="text-center mb-4">
                    <h2 className="text-xl font-heading font-semibold">Récapitulatif</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Vérifiez les informations avant d'envoyer
                    </p>
                  </div>
                  <div className="space-y-3 p-4 rounded-xl" style={{ backgroundColor: `${primaryColor}10` }}>
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">RDV</p>
                        <p className="font-medium">
                          {quoteData.appointment_date?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                          {' '}à {quoteData.appointment_time}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Flower className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Services</p>
                        <p className="font-medium">
                          {amenagementServices.filter(s => quoteData.selected_services.includes(s.id)).map(s => s.name).join(', ')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Contact</p>
                        <p className="font-medium">{quoteData.client_name}</p>
                        <p className="text-sm">{quoteData.client_email}</p>
                        {quoteData.client_phone && <p className="text-sm">{quoteData.client_phone}</p>}
                      </div>
                    </div>
                    {quoteData.client_address && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Adresse</p>
                          <p className="font-medium">{quoteData.client_address}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex justify-between mt-6 pt-4 border-t">
                <Button variant="ghost" onClick={prevQuoteStep}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Retour
                </Button>
                {quoteStep < QUOTE_STEPS.length - 1 ? (
                  <Button onClick={nextQuoteStep} style={primaryBtnStyle}>
                    Continuer
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleQuoteSubmit} 
                    disabled={isSubmitting}
                    style={primaryBtnStyle}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Envoyer la demande
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Regular booking flow (Entretien path)

  return (
    <div className="p-4 md:p-6" style={containerStyle}>
      <div className="max-w-2xl mx-auto">
        {/* Header - Hidden when embedded */}
        {!isEmbedded && (
          <div className="text-center mb-8">
            {config.company.logo_url && config.widget.show_logo !== false ? (
              <div
                className="w-32 h-32 mx-auto mb-4 rounded-full bg-white flex items-center justify-center overflow-hidden p-3 border-2 transition-all duration-300 hover:scale-105"
                style={{ borderColor: primaryColor, boxShadow: `0 4px 24px ${primaryColor}33` }}
              >
                <img 
                  src={getImageUrl(config.company.logo_url)}
                  alt={config.company.name}
                  className="max-w-full max-h-full object-contain" loading="lazy" decoding="async"
                />
              </div>
            ) : config.widget.show_logo !== false && (
              <div 
                className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
                style={{ backgroundColor: primaryColor }}
              >
                <Leaf className="w-7 h-7 text-white" />
              </div>
            )}
            <h1 className="text-2xl font-heading font-semibold">{config.company.name}</h1>
            <p style={{ color: `${textColor}70` }} className="mt-1">{config.widget.name || 'Réservation en ligne'}</p>
          </div>
        )}

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8 px-2 overflow-x-auto">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center shrink-0">
              <div 
                className="flex items-center justify-center w-7 h-7 sm:w-10 sm:h-10 rounded-full transition-colors"
                style={index <= currentStep 
                  ? { backgroundColor: primaryColor, color: '#ffffff' }
                  : { backgroundColor: `${textColor}10`, color: `${textColor}50` }
                }
              >
                {index < currentStep ? (
                  <Check className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
                ) : (
                  <step.icon className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
                )}
              </div>
              {index < STEPS.length - 1 && (
                <div 
                  className="w-6 sm:w-12 md:w-24 h-1 mx-1 sm:mx-2 rounded"
                  style={{ backgroundColor: index < currentStep ? primaryColor : `${textColor}15` }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading">{STEPS[currentStep].title}</CardTitle>
            <CardDescription>
              {currentStep === 0 && "Choisissez le service souhaité"}
              {currentStep === 1 && "Sélectionnez une date et un horaire disponible"}
              {currentStep === 2 && "Vos coordonnées"}
              {currentStep === 3 && "Vérifiez et confirmez votre réservation"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 0: Service Selection - Multiple */}
            {currentStep === 0 && (
              <div className="space-y-4">
                <div className="space-y-3">
                  {config.services.map((service) => {
                    const pricingMode = service.pricing_mode || service.price_type || 'hourly';
                    // Widget-level price override (per_service mode)
                    const widgetServicePrice = widgetPricingMode === 'per_service' ? widgetServicePrices[service.id] : undefined;
                    const getServiceImageUrl = (url) => {
                      if (!url) return null;
                      if (url.startsWith('http')) return url;
                      if (url.startsWith('/uploads')) {
                        return getAssetUrl(url);
                      }
                      return url;
                    };
                    return (
                    <div
                      key={service.id}
                      className="flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors"
                      style={{
                        borderColor: formData.service_ids.includes(service.id) ? primaryColor : `${textColor}20`,
                        backgroundColor: formData.service_ids.includes(service.id) ? `${primaryColor}08` : 'transparent'
                      }}
                      onClick={() => toggleService(service.id)}
                      data-testid={`service-${service.id}`}
                    >
                      <Checkbox 
                        checked={formData.service_ids.includes(service.id)}
                        onCheckedChange={() => toggleService(service.id)}
                      />
                      {/* Service Image */}
                      {service.image_url ? (
                        <img 
                          src={getServiceImageUrl(service.image_url)} 
                          alt={service.name}
                          className="w-12 h-12 rounded-full object-cover border-2"
                          style={{ borderColor: `${textColor}20` }}
                        />
                      ) : (
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${primaryColor}15` }}
                        >
                          <Leaf className="w-5 h-5" style={{ color: primaryColor }} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <p className="font-medium">{service.name}</p>
                          <ServicePriceTag
                            service={service}
                            widgetPricingMode={widgetPricingMode}
                            widgetServicePrice={widgetServicePrice}
                            pricingMode={pricingMode}
                            primaryColor={primaryColor}
                            textColor={textColor}
                          />
                        </div>
                        {service.description && (
                          <p className="text-sm mt-1" style={{ color: `${textColor}60` }}>
                            {service.description}
                          </p>
                        )}
                        {service.duration_minutes && (
                          <p className="text-xs mt-1" style={{ color: `${textColor}50` }}>
                            Durée estimée : {service.duration_minutes} min
                          </p>
                        )}
                        {service.category && (
                          <Badge variant="secondary" className="mt-2">{service.category}</Badge>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>

                {/* Collaborator preference */}
                {serviceCollaborators.length > 0 && (
                  <div className="space-y-3 pt-4 border-t">
                    <Label>Intervenant préféré (optionnel)</Label>
                    <Select
                      value={formData.preferred_collaborator_id || "none"}
                      onValueChange={(value) => setFormData({ ...formData, preferred_collaborator_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="collaborator-select">
                        <SelectValue placeholder="Pas de préférence" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Pas de préférence</SelectItem>
                        {serviceCollaborators.map((collab) => {
                          const isAvailable = !formData.date || filteredCollaborators.some(c => c.id === collab.id);
                          return (
                            <SelectItem
                              key={collab.id}
                              value={collab.id}
                              disabled={!isAvailable}
                              data-testid={`collab-option-${collab.id}${isAvailable ? '' : '-busy'}`}
                            >
                              <div className={`flex items-center gap-2 ${isAvailable ? '' : 'opacity-50'}`}>
                                {collab.avatar_url ? (
                                  <img 
                                    src={getImageUrl(collab.avatar_url)}
                                    alt={collab.name}
                                    className={`w-6 h-6 rounded-full object-cover ${isAvailable ? '' : 'grayscale'}`}
                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                    loading="lazy" decoding="async"
                                  />
                                ) : null}
                                <div 
                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white ${collab.avatar_url ? 'hidden' : ''} ${isAvailable ? '' : 'grayscale'}`}
                                  style={{ backgroundColor: collab.color || primaryColor }}
                                >
                                  {collab.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <span className={isAvailable ? '' : 'line-through'}>{collab.name}</span>
                                {!isAvailable && (
                                  <Badge variant="secondary" className="ml-1 bg-gray-200 text-gray-600 text-[10px] px-1.5">
                                    Occupé
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-xs" style={{ color: `${textColor}50` }}>
                      {formData.date
                        ? `${filteredCollaborators.length} disponible${filteredCollaborators.length > 1 ? 's' : ''} sur ${serviceCollaborators.length} intervenant${serviceCollaborators.length > 1 ? 's' : ''} pour ce créneau`
                        : `${serviceCollaborators.length} intervenant${serviceCollaborators.length > 1 ? 's' : ''} pour ce service`}
                    </p>
                    {formData.date && formData.preferred_collaborator_id && !filteredCollaborators.some(c => c.id === formData.preferred_collaborator_id) && (
                      <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800" data-testid="preferred-collab-unavailable-warning">
                        L'intervenant sélectionné n'est pas disponible sur ce créneau. Choisissez-en un autre ou changez de date.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 1: Date & Slot Type Selection */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="space-y-3">
                  <Label>Sélectionnez une date</Label>
                  {workingDays === null ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : (
                  <div className="flex justify-center">
                    <CalendarComponent
                      key={`calendar-${workingDays?.join('-') || 'loading'}`}
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => {
                        setFormData({ ...formData, date, slot_period: '' }); // Reset slot_period when date changes
                        setDateAvailability(null); // Reset availability
                      }}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const maxDate = new Date();
                        maxDate.setDate(maxDate.getDate() + 90); // 90 jours max
                        
                        // Block closures + French holidays
                        if (isDateClosed(date)) return true;

                        // Check if day is a working day
                        const dayOfWeek = date.getDay();
                        // Convert JS day (0=Sunday) to our format (0=Monday)
                        const normalizedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                        const isWorkingDay = workingDays && workingDays.includes(normalizedDay);
                        
                        return date < today || date > maxDate || !isWorkingDay;
                      }}
                      components={{
                        DayContent: ({ date }) => {
                          const dow = date.getDay();
                          const normalizedDay = dow === 0 ? 6 : dow - 1;
                          const periods = workingDaysPeriods?.[normalizedDay];
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const inPast = date < today;
                          const closureReason = getClosureReason(date);
                          return (
                            <div className="flex flex-col items-center justify-center h-8 w-8" title={closureReason || undefined}>
                              <span className="leading-none">{date.getDate()}</span>
                              {closureReason && !inPast ? (
                                <div className="flex gap-0.5 mt-0.5">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title={closureReason} />
                                </div>
                              ) : periods && !inPast ? (
                                <div className="flex gap-0.5 mt-0.5">
                                  {periods.morning?.active && (
                                    <span
                                      className="w-1.5 h-1.5 rounded-full bg-amber-500"
                                      title="Matin disponible"
                                    />
                                  )}
                                  {periods.afternoon?.active && (
                                    <span
                                      className="w-1.5 h-1.5 rounded-full bg-indigo-500"
                                      title="Après-midi disponible"
                                    />
                                  )}
                                  {periods.full_day?.active && (
                                    <span
                                      className="w-1.5 h-1.5 rounded-full bg-emerald-500"
                                      title="Journée complète disponible"
                                    />
                                  )}
                                </div>
                              ) : null}
                            </div>
                          );
                        },
                      }}
                      className="rounded-xl border"
                    />
                  </div>
                  )}
                  <p className="text-xs text-muted-foreground text-center">
                    Réservation possible jusqu'à 90 jours à l'avance
                    {workingDays && workingDays.length < 7 && (
                      <span className="block mt-1">
                        (Jours de fermeture grisés)
                      </span>
                    )}
                  </p>
                  {/* Legend for the period dots */}
                  {Object.keys(workingDaysPeriods || {}).length > 0 && (
                    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Matin
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" /> Après-midi
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Journée complète
                      </span>
                    </div>
                  )}
                </div>

                {formData.date && !config?.widget?.hide_duration_choice && (
                  <div className="space-y-3">
                    <Label>Type de créneau</Label>
                    <div className={`grid gap-3 ${slotTypes.length > 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2'}`}>
                      {slotTypes.map((st) => {
                        const price = getSlotTypePrice(st);
                        const avail = slotTypeAvailability(st);
                        const isSelected = formData.slot_type === (st.code || st.id);
                        return (
                        <Button
                          key={st.code || st.id}
                          variant={isSelected ? 'default' : 'outline'}
                          className="h-auto py-4 flex-col relative"
                          style={isSelected ? primaryBtnStyle : {}}
                          disabled={!avail.available}
                          onClick={() => {
                            if (!avail.available) return;
                            const stCode = st.code || st.id;
                            const codeLow = (st.code || '').toLowerCase();
                            const nameLow = (st.name || '').toLowerCase();
                            // Auto-set slot_period if the slot type already
                            // encodes a specific half-day (matin / apres-midi).
                            let autoPeriod = '';
                            if (codeLow === 'matin' || /^matin\b/.test(nameLow)) autoPeriod = 'morning';
                            else if (/apres|aprem/.test(codeLow) || /apr[eè]s|aprem/.test(nameLow)) autoPeriod = 'afternoon';
                            setFormData({ ...formData, slot_type: stCode, slot_period: autoPeriod });
                          }}
                          data-testid={`slot-${st.code || st.id}`}
                          title={!avail.available ? avail.reason : undefined}
                        >
                          <div className="text-center">
                            <p className="font-medium">{st.name}</p>
                            <p className="text-sm opacity-75">{st.duration_hours} heures</p>
                            {st.required_collaborators > 1 && (
                              <p className="text-xs opacity-75">{st.required_collaborators} intervenants</p>
                            )}
                            {_isTaxCreditEligible ? (
                              <>
                                <p className="text-[9px] uppercase tracking-wide font-semibold opacity-75 mt-1">Reste à charge</p>
                                <p className="text-xl font-bold leading-tight" style={{ color: isSelected ? '#fff' : '#059669' }}>
                                  {(price * 0.5).toFixed(0)}€
                                </p>
                                <p className="text-[11px] opacity-60 line-through leading-tight">
                                  {price.toFixed(0)}€<sup className="no-underline">*</sup>
                                </p>
                                {widgetPricingMode !== 'per_slot_type' && (
                                  <p className="text-[10px] opacity-60">
                                    {st.required_collaborators > 1 
                                      ? `${defaultHourlyRate}€/h × ${st.duration_hours}h × ${st.required_collaborators}`
                                      : `${defaultHourlyRate}€/h × ${st.duration_hours}h`
                                    }
                                  </p>
                                )}
                              </>
                            ) : (
                              <>
                                <p className="text-lg font-bold mt-1">{price.toFixed(0)}€</p>
                                {widgetPricingMode === 'per_slot_type' ? (
                                  <p className="text-[10px] opacity-60">Tarif forfaitaire</p>
                                ) : (
                                  <p className="text-[10px] opacity-60">
                                    {st.required_collaborators > 1 
                                      ? `${defaultHourlyRate}€/h × ${st.duration_hours}h × ${st.required_collaborators}`
                                      : `${defaultHourlyRate}€/h × ${st.duration_hours}h`
                                    }
                                  </p>
                                )}
                              </>
                            )}
                            {!avail.available && (
                              <p className="text-[10px] mt-1 font-medium text-red-500">
                                {avail.reason}
                              </p>
                            )}
                          </div>
                        </Button>
                      )})}
                    </div>
                    {widgetPricingMode === 'per_slot_type' ? (
                      <p className="text-xs text-muted-foreground text-center">
                        Prix forfaitaire par créneau
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground text-center">
                        Prix indicatif basé sur {defaultHourlyRate}€/h
                      </p>
                    )}
                    {_isTaxCreditEligible && (
                      <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 leading-relaxed text-center" data-testid="punctual-tax-credit-footnote">
                        <span className="font-semibold">*</span> Le reste à charge tient compte de l&apos;<span className="font-semibold">avance immédiate du crédit d&apos;impôt de 50%</span> appliquée directement par l&apos;URSSAF. Vous ne payez que la moitié, sans attendre votre déclaration.
                      </p>
                    )}
                  </div>
                )}
                
                {/* When duration choice is hidden, show a simple time preference instead */}
                {formData.date && config?.widget?.hide_duration_choice && (
                  <div className="space-y-3">
                    <Label>Créneau horaire préféré</Label>
                    <div className="grid grid-cols-3 gap-3">
                      <Button
                        variant={formData.slot_period === 'morning' ? 'default' : 'outline'}
                        className="h-auto py-3"
                        style={formData.slot_period === 'morning' ? primaryBtnStyle : {}}
                        onClick={() => setFormData({ ...formData, slot_type: '', slot_period: 'morning' })}
                      >
                        <div className="text-center">
                          <p className="font-medium text-sm">Matin</p>
                        </div>
                      </Button>
                      <Button
                        variant={formData.slot_period === 'afternoon' ? 'default' : 'outline'}
                        className="h-auto py-3"
                        style={formData.slot_period === 'afternoon' ? primaryBtnStyle : {}}
                        onClick={() => setFormData({ ...formData, slot_type: '', slot_period: 'afternoon' })}
                      >
                        <div className="text-center">
                          <p className="font-medium text-sm">Après-midi</p>
                        </div>
                      </Button>
                      <Button
                        variant={formData.slot_period === 'flexible' ? 'default' : 'outline'}
                        className="h-auto py-3"
                        style={formData.slot_period === 'flexible' ? primaryBtnStyle : {}}
                        onClick={() => setFormData({ ...formData, slot_type: '', slot_period: 'flexible' })}
                      >
                        <div className="text-center">
                          <p className="font-medium text-sm">Flexible</p>
                        </div>
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      La durée sera déterminée par l'entreprise selon vos besoins
                    </p>
                  </div>
                )}

                {/* Morning/Afternoon choice for half day - with dynamic availability */}
                {formData.date && formData.slot_type === 'half_day' && !config?.widget?.hide_duration_choice && (
                  <div className="space-y-3">
                    <Label>Choisissez votre créneau</Label>
                    {loadingAvailability ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: primaryColor }}></div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        <Button
                          variant={formData.slot_period === 'morning' ? 'default' : 'outline'}
                          className="h-auto py-4"
                          style={formData.slot_period === 'morning' ? secondaryBtnStyle : {}}
                          onClick={() => setFormData({ ...formData, slot_period: 'morning' })}
                          disabled={dateAvailability && !dateAvailability.morning_available}
                          data-testid="slot-morning"
                        >
                          <div className="text-center">
                            <p className="font-medium">Matin</p>
                            <p className="text-sm opacity-75">
                              {dateAvailability?.morning_display || '8h - 12h'}
                            </p>
                            {dateAvailability && !dateAvailability.morning_available && (
                              <p className="text-xs text-red-500 mt-1">Indisponible</p>
                            )}
                          </div>
                        </Button>
                        <Button
                          variant={formData.slot_period === 'afternoon' ? 'default' : 'outline'}
                          className="h-auto py-4"
                          style={formData.slot_period === 'afternoon' ? secondaryBtnStyle : {}}
                          onClick={() => setFormData({ ...formData, slot_period: 'afternoon' })}
                          disabled={dateAvailability && !dateAvailability.afternoon_available}
                          data-testid="slot-afternoon"
                        >
                          <div className="text-center">
                            <p className="font-medium">Après-midi</p>
                            <p className="text-sm opacity-75">
                              {dateAvailability?.afternoon_display || '14h - 18h'}
                            </p>
                            {dateAvailability && !dateAvailability.afternoon_available && (
                              <p className="text-xs text-red-500 mt-1">Indisponible</p>
                            )}
                          </div>
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {selectedServices.length > 0 && formData.date && formData.slot_type && (!_isAmbiguousHalfDay || formData.slot_period) && (
                  <div className="p-4 rounded-xl" style={{ backgroundColor: `${primaryColor}10` }}>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">
                          {selectedServices.map(s => s.name).join(', ')}
                        </p>
                        <p className="text-sm" style={{ color: `${textColor}60` }}>
                          {formData.date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </p>
                        <p className="text-sm" style={{ color: `${textColor}60` }}>
                          {_isFullDaySelection
                            ? `${currentSlotType?.name || 'Journée complète'} (${getSlotHours()}h)`
                            : _isMorningSelection
                              ? `${currentSlotType?.name || 'Matin'} (${dateAvailability?.morning_display || '8h - 12h'})`
                              : _isAfternoonSelection
                                ? `${currentSlotType?.name || 'Après-midi'} (${dateAvailability?.afternoon_display || '14h - 18h'})`
                                : `${currentSlotType?.name || 'Demi-journée'} - ${formData.slot_period === 'morning' ? `Matin (${dateAvailability?.morning_display || '8h-12h'})` : `Après-midi (${dateAvailability?.afternoon_display || '14h-18h'})`}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm" style={{ color: `${textColor}60` }}>Durée totale</p>
                        <p className="text-2xl font-semibold" style={{ color: primaryColor }}>
                          {getSlotHours()}h
                        </p>
                        <div className="mt-2">
                          <PriceBlock price={calculatePrice()} size="md" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Collaborator preference — shown when we know date+slot so
                    we can grey out busy intervenants in real time. */}
                {formData.date && formData.slot_type && (formData.slot_period || formData.slot_type === 'full_day') && serviceCollaborators.length > 0 && (
                  <div className="space-y-3 pt-4 border-t" data-testid="step1-collab-picker">
                    <Label>Intervenant préféré (optionnel)</Label>
                    <Select
                      value={formData.preferred_collaborator_id || "none"}
                      onValueChange={(value) => setFormData({ ...formData, preferred_collaborator_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="step1-collaborator-select">
                        <SelectValue placeholder="Pas de préférence" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Pas de préférence</SelectItem>
                        {serviceCollaborators.map((collab) => {
                          const isAvailable = filteredCollaborators.some(c => c.id === collab.id);
                          return (
                            <SelectItem
                              key={collab.id}
                              value={collab.id}
                              disabled={!isAvailable}
                              data-testid={`step1-collab-${collab.id}${isAvailable ? '' : '-busy'}`}
                            >
                              <div className={`flex items-center gap-2 ${isAvailable ? '' : 'opacity-50'}`}>
                                {collab.avatar_url ? (
                                  <img
                                    src={getImageUrl(collab.avatar_url)}
                                    alt={collab.name}
                                    className={`w-6 h-6 rounded-full object-cover ${isAvailable ? '' : 'grayscale'}`}
                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                    loading="lazy" decoding="async"
                                  />
                                ) : null}
                                <div
                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white ${collab.avatar_url ? 'hidden' : ''} ${isAvailable ? '' : 'grayscale'}`}
                                  style={{ backgroundColor: collab.color || primaryColor }}
                                >
                                  {collab.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <span className={isAvailable ? '' : 'line-through'}>{collab.name}</span>
                                {!isAvailable && (
                                  <Badge variant="secondary" className="ml-1 bg-gray-200 text-gray-600 text-[10px] px-1.5">
                                    Occupé
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-xs" style={{ color: `${textColor}50` }}>
                      {filteredCollaborators.length} disponible{filteredCollaborators.length > 1 ? 's' : ''} sur {serviceCollaborators.length} intervenant{serviceCollaborators.length > 1 ? 's' : ''} pour ce créneau
                    </p>
                    {formData.preferred_collaborator_id && !filteredCollaborators.some(c => c.id === formData.preferred_collaborator_id) && (
                      <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800" data-testid="step1-preferred-unavailable">
                        L'intervenant sélectionné n'est pas disponible sur ce créneau. Choisissez-en un autre ou changez de date.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Client Information */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nom complet *</Label>
                    <Input
                      data-testid="client-name"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      data-testid="client-email"
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                      placeholder="jean@email.fr"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    data-testid="client-phone"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    placeholder="06 12 34 56 78"
                  />
                </div>
                
                {/* Custom Questions */}
                {config?.custom_questions && config.custom_questions.length > 0 && (
                  <div className="space-y-4 pt-4 border-t">
                    <p className="font-medium text-sm" style={{ color: primaryColor }}>Questions supplémentaires</p>
                    {config.custom_questions.map((question, index) => (
                      <div key={question.id || index} className="space-y-2">
                        <Label>{question.question}{question.required && ' *'}</Label>
                        {question.type === 'text' && (
                          <Input
                            value={formData.custom_answers?.[question.id] || ''}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              custom_answers: { ...formData.custom_answers, [question.id]: e.target.value } 
                            })}
                            placeholder={question.placeholder || 'Votre réponse...'}
                          />
                        )}
                        {question.type === 'textarea' && (
                          <Textarea
                            value={formData.custom_answers?.[question.id] || ''}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              custom_answers: { ...formData.custom_answers, [question.id]: e.target.value } 
                            })}
                            placeholder={question.placeholder || 'Votre réponse...'}
                            rows={3}
                          />
                        )}
                        {question.type === 'select' && question.options && (
                          <Select
                            value={formData.custom_answers?.[question.id] || ''}
                            onValueChange={(value) => setFormData({ 
                              ...formData, 
                              custom_answers: { ...formData.custom_answers, [question.id]: value } 
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Choisir une option" />
                            </SelectTrigger>
                            <SelectContent>
                              {question.options.map((opt, optIndex) => (
                                <SelectItem key={optIndex} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {question.type === 'checkbox' && (
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.custom_answers?.[question.id] === 'true'}
                              onCheckedChange={(checked) => setFormData({ 
                                ...formData, 
                                custom_answers: { ...formData.custom_answers, [question.id]: checked ? 'true' : 'false' } 
                              })}
                            />
                            <span className="text-sm">{question.label || 'Oui'}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Notes ou demandes particulières</Label>
                  <Textarea
                    data-testid="client-notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Informations complémentaires..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="p-4 rounded-xl space-y-4" style={{ backgroundColor: `${textColor}05` }}>
                  <p className="font-medium">Récapitulatif de votre réservation</p>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p style={{ color: `${textColor}60` }}>Service(s)</p>
                      <p className="font-medium">{selectedServices.map(s => s.name).join(', ')}</p>
                    </div>
                    <div>
                      <p style={{ color: `${textColor}60` }}>Date</p>
                      <p className="font-medium">
                        {formData.date?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: `${textColor}60` }}>Créneau</p>
                      <p className="font-medium">
                        {formData.slot_type === 'full_day' ? 'Journée complète' : 'Demi-journée'}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: `${textColor}60` }}>Durée totale</p>
                      <p className="font-medium">{getSlotHours()} heures</p>
                    </div>
                  </div>

                  {/* Recurrence info in confirmation */}
                  {formData.is_recurring && formData.recurrence_type && (
                    <div className="border-t pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Repeat className="w-4 h-4" style={{ color: primaryColor }} />
                        <p className="font-medium" style={{ color: primaryColor }}>Réservation récurrente</p>
                      </div>
                      <p className="text-sm" style={{ color: `${textColor}70` }}>
                        {formData.recurrence_type === 'weekly' && `Hebdomadaire${formData.recurrence_days.length > 0 ? ` (${formData.recurrence_days.map(d => ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'][d]).join(', ')})` : ''}`}
                        {formData.recurrence_type === 'biweekly' && `Bi-hebdomadaire${formData.recurrence_days.length > 0 ? ` (${formData.recurrence_days.map(d => ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'][d]).join(', ')})` : ''}`}
                        {formData.recurrence_type === 'monthly' && 'Mensuelle'}
                        {formData.recurrence_type === 'custom' && `Tous les ${formData.recurrence_interval} jours`}
                        {formData.recurrence_end_type === 'count' && ` - ${formData.recurrence_count} séances`}
                        {formData.recurrence_end_type === 'date' && formData.recurrence_end_date && ` - jusqu'au ${new Date(formData.recurrence_end_date).toLocaleDateString('fr-FR')}`}
                        {formData.recurrence_end_type === 'unlimited' && ' - sans limite'}
                      </p>
                    </div>
                  )}

                  <div className="border-t pt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p style={{ color: `${textColor}60` }}>Nom</p>
                      <p className="font-medium">{formData.client_name}</p>
                    </div>
                    <div>
                      <p style={{ color: `${textColor}60` }}>Email</p>
                      <p className="font-medium">{formData.client_email}</p>
                    </div>
                    {formData.client_phone && (
                      <div>
                        <p style={{ color: `${textColor}60` }}>Téléphone</p>
                        <p className="font-medium">{formData.client_phone}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4 rounded-xl border" style={{ borderColor: `${primaryColor}30`, backgroundColor: `${primaryColor}05` }}>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm" style={{ color: `${textColor}60` }}>
                        {formData.is_recurring && formData.recurrence_type 
                          ? (
                              widgetPricingMode === 'per_slot_type'
                                ? 'Prix par séance (forfait créneau)'
                                : widgetPricingMode === 'per_service'
                                  ? 'Prix par séance (forfait service)'
                                  : `Prix par séance (${getSlotHours()}h × ${defaultHourlyRate}€/h)`
                            )
                          : (
                              widgetPricingMode === 'per_slot_type'
                                ? 'Total à payer (forfait créneau)'
                                : widgetPricingMode === 'per_service'
                                  ? 'Total à payer (forfait service)'
                                  : `Total à payer (${getSlotHours()}h × ${defaultHourlyRate}€/h)`
                            )
                        }
                      </p>
                      <PriceBlock
                        price={calculatePrice()}
                        suffix={formData.is_recurring && formData.recurrence_type && formData.recurrence_end_type === 'count' ? (
                          <span className="text-sm font-normal ml-2" style={{ color: `${textColor}60` }}>
                            × {formData.recurrence_count} = {(calculatePrice() * formData.recurrence_count * (_isTaxCreditEligible ? 0.5 : 1)).toFixed(2)}€ total
                          </span>
                        ) : null}
                      />
                    </div>
                    {config.widget.booking_mode === 'approval' && (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Validation requise
                      </Badge>
                    )}
                  </div>
                </div>

                {/* CGV Checkbox */}
                {config.widget.terms_enabled && config.widget.terms_link && (
                  <div className="flex items-start gap-3 p-4 rounded-xl border" style={{ borderColor: termsAccepted ? primaryColor : `${textColor}20` }}>
                    <Checkbox
                      id="terms-acceptance"
                      checked={termsAccepted}
                      onCheckedChange={(checked) => setTermsAccepted(checked)}
                      data-testid="terms-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="terms-acceptance" className="cursor-pointer text-sm leading-relaxed">
                        {config.widget.terms_text || "J'accepte les conditions générales de vente"}{' '}
                        <a 
                          href={config.widget.terms_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="underline font-medium"
                          style={{ color: primaryColor }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Lire les CGV
                        </a>
                      </Label>
                    </div>
                  </div>
                )}

                {config.widget.booking_mode === 'approval' && (
                  <p className="text-sm text-center" style={{ color: `${textColor}60` }}>
                    Votre demande sera envoyée à {config.company.name} pour validation. 
                    Vous recevrez une confirmation par email.
                  </p>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 0}
                className="rounded-full"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Précédent
              </Button>
              
              {currentStep < STEPS.length - 1 ? (
                <Button 
                  onClick={nextStep} 
                  className="rounded-full" 
                  data-testid="next-step-btn"
                  style={primaryBtnStyle}
                >
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={requiresPayment() ? handlePayment : handleSubmit} 
                  className="rounded-full"
                  style={secondaryBtnStyle}
                  disabled={isSubmitting || !formData.client_name || !formData.client_email || (config?.widget?.terms_enabled && config?.widget?.terms_link && !termsAccepted)}
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {requiresPayment() ? 'Redirection...' : 'Envoi...'}
                    </>
                  ) : requiresPayment() ? (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Payer {calculatePrice().toFixed(2)}€
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {config.widget.booking_mode === 'direct' ? 'Confirmer' : 'Envoyer la demande'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer - Propulsé par ResaWork + Widget ID (hidden for Premium subscribers) */}
        <WidgetFooter widgetId={widgetId} isPremium={config?.is_premium} />
      </div>
    </div>
  );
}
